import { z } from "zod";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

const SP_DESCRIPTION =
  "Story point value (1=Common, 2=Magic, 3=Rare, 5=Unique). The key is 'storyPointValue' — not 'storyPoints'.";

const VALID_PROPERTY_KEYS = "title, storyPointValue, tagNames, githubPRUrl, githubBranch, risk";

interface TaskPropertyParams {
  title?: string;
  storyPointValue?: number;
  tagNames?: string[];
  githubPRUrl?: string;
  githubBranch?: string;
  risk?: "critical" | "high" | "medium" | "low" | null;
}

/** Human-readable summary of the fields an update touched. */
function describeUpdatedFields(p: TaskPropertyParams): string[] {
  const fields: string[] = [];
  if (p.title !== undefined) fields.push(`title to "${p.title}"`);
  if (p.storyPointValue !== undefined) fields.push(`story points to ${p.storyPointValue}`);
  if (p.tagNames !== undefined) fields.push(`tags (${p.tagNames.length} tag(s))`);
  if (p.githubPRUrl !== undefined) fields.push(`PR link to "${p.githubPRUrl}"`);
  if (p.githubBranch !== undefined) fields.push(`branch to "${p.githubBranch}"`);
  if (p.risk !== undefined) fields.push(`risk to ${p.risk ?? "cleared"}`);
  return fields;
}

export function buildDiscoveryTools(connection: AgentConnection) {
  return [
    defineTool(
      "update_task_properties",
      "Set one or more task properties in a single call. Valid keys: title, storyPointValue, tagNames, githubPRUrl, githubBranch, risk. All are optional — include only the ones you want to update (at least one). Unknown keys are rejected.",
      {
        title: z.string().optional().describe("The new task title"),
        storyPointValue: z.number().optional().describe(SP_DESCRIPTION),
        tagNames: z.array(z.string()).optional().describe("Array of tag names to assign"),
        githubPRUrl: z
          .string()
          .url()
          .optional()
          .describe("GitHub pull request URL to link to this task"),
        githubBranch: z
          .string()
          .optional()
          .describe("Set the GitHub branch name for this task (e.g. 'conveyor/my-feature-abc123')"),
        risk: z
          .enum(["critical", "high", "medium", "low"])
          .nullable()
          .optional()
          .describe(
            "Risk level — how much important surface the task touches (critical/high/medium/low). Pass null to clear.",
          ),
      },
      async ({ title, storyPointValue, tagNames, githubPRUrl, githubBranch, risk }) => {
        try {
          const params: TaskPropertyParams = {
            title,
            storyPointValue,
            tagNames,
            githubPRUrl,
            githubBranch,
            risk,
          };
          const updatedFields = describeUpdatedFields(params);
          if (updatedFields.length === 0) {
            // Zod strips unrecognized keys on harnesses that don't enforce the
            // strict schema, so a misspelled key (e.g. "storyPoints") arrives
            // here as an empty update. Fail loudly instead of no-oping.
            return textResult(
              `No task properties were updated: none of the recognized keys were provided. ` +
                `Valid keys: ${VALID_PROPERTY_KEYS}. ` +
                `(Story points are set via 'storyPointValue', not 'storyPoints'.)`,
            );
          }

          await connection.call("updateTaskProperties", {
            sessionId: connection.sessionId,
            ...params,
          });

          return textResult(`Task properties updated: ${updatedFields.join(", ")}`);
        } catch (error) {
          return textResult(
            `Failed to update task properties: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
      // Reject unknown keys (e.g. "storyPoints") at validation instead of
      // stripping them into a silent no-op — every field here is optional, so
      // a stripped typo would otherwise report success while updating nothing.
      { strict: true },
    ),
  ];
}
